import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

const validDate = (value: string | null): value is string => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00.000Z`);
  return (
    !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value
  );
};

export function getDefaultReportDates(now = new Date()) {
  const start = new Date(now);
  start.setUTCDate(start.getUTCDate() - 29);
  return {
    startDate: start.toISOString().slice(0, 10),
    endDate: now.toISOString().slice(0, 10),
  };
}

export function useRequestReportDateRange() {
  const [params, setParams] = useSearchParams();
  const defaults = useMemo(() => getDefaultReportDates(), []);
  const startParam = params.get('startDate');
  const endParam = params.get('endDate');
  const startDate = validDate(startParam) ? startParam : defaults.startDate;
  const requestedEnd = validDate(endParam) ? endParam : defaults.endDate;
  const endDate = requestedEnd < startDate ? startDate : requestedEnd;
  const changeDate = (field: 'startDate' | 'endDate', value: string) => {
    if (!validDate(value)) return;
    const next = new URLSearchParams(params);
    next.set(
      'startDate',
      field === 'startDate' ? value : value < startDate ? value : startDate,
    );
    next.set(
      'endDate',
      field === 'endDate' ? value : value > endDate ? value : endDate,
    );
    setParams(next, { replace: true });
  };
  const nextDay = new Date(`${endDate}T00:00:00.000Z`);
  nextDay.setUTCDate(nextDay.getUTCDate() + 1);
  return {
    startDate,
    endDate,
    startDateTime: `${startDate}T00:00:00.000Z`,
    endDateTime: `${endDate}T23:59:59.999Z`,
    exclusiveEndDateTime: nextDay.toISOString(),
    changeDate,
  };
}

export function RequestReportDateRange({
  startDate,
  endDate,
  changeDate,
}: ReturnType<typeof useRequestReportDateRange>) {
  return (
    <div
      role="group"
      aria-label="Report dates (UTC)"
      className="flex max-w-full flex-wrap items-end gap-3 rounded-xl border border-gray-200 bg-white p-3 shadow-sm"
    >
      <label className="min-w-0 text-xs font-medium text-gray-600">
        Start date
        <input
          type="date"
          value={startDate}
          aria-describedby="report-dates-timezone"
          onChange={(event) => changeDate('startDate', event.target.value)}
          className="mt-1 block min-w-0 max-w-full rounded-lg border border-gray-200 bg-gray-50 px-2 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </label>
      <label className="min-w-0 text-xs font-medium text-gray-600">
        End date
        <input
          type="date"
          value={endDate}
          aria-describedby="report-dates-timezone"
          onChange={(event) => changeDate('endDate', event.target.value)}
          className="mt-1 block min-w-0 max-w-full rounded-lg border border-gray-200 bg-gray-50 px-2 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </label>
      <span
        aria-hidden="true"
        className="mb-2 rounded bg-gray-100 px-2 py-1 text-xs font-medium text-gray-500"
      >
        UTC
      </span>
      <p id="report-dates-timezone" className="sr-only">
        Inclusive dates, UTC.
      </p>
    </div>
  );
}
